import type { Message } from 'ai';
import { useCallback, useState } from 'react';
import { EnhancedStreamingMessageParser } from '~/lib/runtime/enhanced-message-parser';
import { workbenchStore } from '~/lib/stores/workbench';
import { versionsStore } from '~/lib/stores/versions';
import { structuredEventProcessor } from '~/lib/stores/streaming';
import { runtimeContext } from '~/lib/runtime';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('useMessageParser');

// Track which messages have already had versions created
const versionedMessages = new Set<string>();

// Debounce timer for version creation (wait for all artifacts in a message to complete)
let versionDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingVersionData: { messageId: string; title: string } | null = null;

/**
 * Reset version tracking state. Call when starting a new chat session
 * to prevent stale message IDs from accumulating indefinitely.
 */
export function resetVersionTracking() {
  versionedMessages.clear();

  if (versionDebounceTimer) {
    clearTimeout(versionDebounceTimer);
    versionDebounceTimer = null;
  }

  pendingVersionData = null;
}

const messageParser = new EnhancedStreamingMessageParser({
  callbacks: {
    onArtifactOpen: (data) => {
      logger.trace('onArtifactOpen', data);

      workbenchStore.showWorkbench.set(true);
      workbenchStore.addArtifact(data);
    },
    onArtifactClose: (data) => {
      logger.trace('onArtifactClose');

      workbenchStore.updateArtifact(data, { closed: true });

      // Only create one version per message (debounced)
      const artifact = data.artifactId ? workbenchStore.artifacts.get()[data.artifactId] : undefined;
      const messageId = data.messageId || artifact?.id || '';

      /*
       * Skip version snapshot for preloaded artifacts (e.g. git clone import).
       * Files haven't changed — the snapshot would be identical to the initial state.
       */
      if (artifact?.preloaded) {
        logger.debug('Skipping version snapshot for preloaded artifact:', data.artifactId);
        return;
      }

      // Skip if we've already versioned this message
      if (versionedMessages.has(messageId)) {
        return;
      }

      // Store the pending version data
      pendingVersionData = {
        messageId,
        title: artifact?.title || 'Project Update',
      };

      // Clear any existing timer
      if (versionDebounceTimer) {
        clearTimeout(versionDebounceTimer);
      }

      /*
       * Wait 500ms after last artifact closes before creating version
       * This groups all artifacts from one message into a single version
       */
      versionDebounceTimer = setTimeout(() => {
        if (!pendingVersionData) {
          return;
        }

        const { messageId: versionMessageId, title } = pendingVersionData;

        // Mark this message as versioned
        versionedMessages.add(versionMessageId);

        const files = workbenchStore.files.get();
        const fileSnapshot: Record<string, { content: string; type: string }> = {};

        for (const [path, dirent] of Object.entries(files)) {
          if (dirent?.type === 'file' && !dirent.isBinary) {
            fileSnapshot[path] = {
              content: dirent.content || '',
              type: 'file',
            };
          }
        }

        const version = versionsStore.createVersion(versionMessageId, title, `Completed: ${title}`, fileSnapshot);

        logger.trace('Version created for message:', versionMessageId);

        // Auto-commit to git (fire-and-forget, non-blocking)
        if (runtimeContext.projectId) {
          import('~/lib/runtime/git-client')
            .then(({ commit }) => {
              commit(runtimeContext.projectId!, title)
                .then((sha) => {
                  if (sha) {
                    versionsStore.updateCommitSha(version.id, sha);
                  }
                })
                .catch((error) => {
                  logger.warn('Git commit failed (best-effort, non-blocking):', error);
                });
            })
            .catch((error) => {
              logger.warn('Failed to load git-client module:', error);
            });
        }

        // Capture thumbnail in the background with retries (preview may not be ready yet)
        versionsStore.scheduleThumbnailCapture(version.id);

        pendingVersionData = null;
      }, 500);
    },
    onActionOpen: (data) => {
      logger.trace('onActionOpen', data.action);

      /*
       * During session restore, we add actions for display purposes only.
       * File actions: added as completed (snapshot handles actual file restore).
       * Shell/start actions: added as completed (auto-rebuild handles npm install/dev server).
       */
      const isRestoring = workbenchStore.isRestoringSession.get();
      const isReloadedMsg = workbenchStore.isReloadedMessage(data.messageId);

      if (isRestoring && isReloadedMsg) {
        // During restore, skip open — we handle everything in onActionClose
        return;
      }

      /*
       * File actions are streamed, so we add them immediately to show progress
       * Shell actions are complete when created by enhanced parser, so we wait for close
       */
      if (data.action.type === 'file') {
        workbenchStore.addAction(data);
      }
    },
    onActionClose: (data) => {
      logger.trace('onActionClose', data.action);

      /*
       * During session restore, restore actions as completed for display only.
       * No file writes or shell command execution — snapshot + auto-rebuild handle that.
       */
      const isRestoring = workbenchStore.isRestoringSession.get();
      const isReloadedMsg = workbenchStore.isReloadedMessage(data.messageId);

      if (isRestoring && isReloadedMsg) {
        workbenchStore.restoreAction(data);
        return;
      }

      /*
       * Add non-file actions (shell, build, start, etc.) when they close
       * Enhanced parser creates complete shell actions, so they're ready to execute
       */
      if (data.action.type !== 'file') {
        workbenchStore.addAction(data);
      }

      workbenchStore.runAction(data);
    },
    onActionStream: (data) => {
      logger.trace('onActionStream', data.action);

      /*
       * Skip file streaming during session restore — snapshot handles file content.
       */
      const isRestoring = workbenchStore.isRestoringSession.get();
      const isReloadedMsg = workbenchStore.isReloadedMessage(data.messageId);

      if (isRestoring && isReloadedMsg) {
        return;
      }

      workbenchStore.runAction(data, true);
    },
  },
});

/*
 * Register the parser's structured event handler so WorkbenchStore.processDataStreamItems
 * can forward validated streaming events without a circular import.
 */
structuredEventProcessor.set((event) => messageParser.processStructuredEvent(event));

const extractTextContent = (message: Message) =>
  Array.isArray(message.content)
    ? (message.content.find((item) => item.type === 'text')?.text as string) || ''
    : message.content;

export function useMessageParser() {
  const [parsedMessages, setParsedMessages] = useState<{ [key: number]: string }>({});

  const parseMessages = useCallback((messages: Message[], isLoading: boolean) => {
    let reset = false;
    const updates: Array<{ index: number; content: string; replace: boolean }> = [];

    if (import.meta.env.DEV && !isLoading) {
      reset = true;
      messageParser.reset();
      resetVersionTracking();
    }

    for (const [index, message] of messages.entries()) {
      if (message.role === 'assistant' || message.role === 'user') {
        const newParsedContent = messageParser.parse(message.id, extractTextContent(message));

        /*
         * Check if the enhanced parser internally reset (e.g., when wrapping code blocks in artifact tags).
         * When this happens, we need to REPLACE the previous content, not append to it,
         * to avoid duplicate content during streaming.
         */
        const parserDidReset = messageParser.didResetOccur();
        const shouldReplace = reset || parserDidReset;

        // DEBUG: Log reset detection
        if (parserDidReset) {
          logger.debug('Parser reset detected for message', message.id, 'shouldReplace:', shouldReplace);
          logger.debug('New content length:', newParsedContent.length);
        }

        if (newParsedContent || shouldReplace) {
          updates.push({ index, content: newParsedContent, replace: shouldReplace });
        }
      }
    }

    if (updates.length > 0) {
      setParsedMessages((previous) => {
        const next = { ...previous };

        for (const update of updates) {
          next[update.index] = update.replace ? update.content : (next[update.index] || '') + update.content;
        }

        return next;
      });
    }
  }, []);

  return { parsedMessages, parseMessages };
}
